import mapConfig from '../config/map-config'
import { basemapTypes } from '../lib/constants'

export const createBasemaps = (esriLibrary: any): any[] => {
  return Object.keys(mapConfig.basemaps).map((basemapKey: string) => {
    let layer
    if (mapConfig.basemaps[basemapKey].type === basemapTypes.esriTile) {
      layer = new esriLibrary.TileLayer({
        url: mapConfig.basemaps[basemapKey].url
      })
    } else if (mapConfig.basemaps[basemapKey].type === basemapTypes.wmts) {
      layer = new esriLibrary.WMTSLayer({
        url: mapConfig.basemaps[basemapKey].url,
        activeLayer: {
          id: mapConfig.basemaps[basemapKey].layerId
        },
        serviceMode: mapConfig.basemaps[basemapKey].serviceMode,
        opacity: mapConfig.basemaps[basemapKey].opacity,
        maxScale: mapConfig.basemaps[basemapKey].maxScale
      })
    }
    return new esriLibrary.Basemap({
      baseLayers: layer,
      title: mapConfig.basemaps[basemapKey].title,
      id: basemapKey,
      thumbnailUrl: mapConfig.basemaps[basemapKey].thumbnail
    })
  })
}

export const getLayerIdFromName = (mapServiceLegendConfig: any, layerName: string): number => {
  const matchingLayer = getLayerFromName(mapServiceLegendConfig, layerName)
  return matchingLayer ? matchingLayer.layerId : -1
}

const getLayerFromName = (mapServiceLegendConfig: any, layerName: string): any => {
  const matchingLayers = mapServiceLegendConfig.layers.filter((x: any) => x.layerName === layerName)
  let matchingLayer = null
  if (matchingLayers.length > 0) {
    matchingLayer = matchingLayers[0]
  }
  return matchingLayer
}

const getDynamicMapServiceLegendConfig = (url: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    window.require(['esri/request'], (esriRequest: any) => {
      esriRequest(`${url}?f=json`, {
        responseType: 'json'
      }).then(
        (response: any) => {
          const legendConfig = response.data
          resolve(legendConfig)
        },
        (reason: any) => {
          reject(reason)
        }
      )
    })
  })
}

const createMapImageLayer = (MapImageLayer: any, dynamicMapServiceKey: string, legendConfigs: any) => {
  return new Promise((resolve, reject) => {
    const mapServiceAppConfig: any = mapConfig.dynamicMapServices[dynamicMapServiceKey]
    let sublayers: any[] = []
    getDynamicMapServiceLegendConfig(`${mapServiceAppConfig.url}/legend`).then(
      (mapServiceLegendConfig: any) => {
        legendConfigs[dynamicMapServiceKey] = mapServiceLegendConfig
        Object.keys(mapServiceAppConfig.layers).forEach((layerKey: any) => {
          const layerName = mapServiceAppConfig.layers[layerKey].name
          const layerId = getLayerIdFromName(mapServiceLegendConfig, layerName)
          if (layerId === -1) {
            throw new Error(`Unable to determine layer id for name ${layerName}`)
          } else {
            sublayers.push({
              id: layerId,
              visible: mapServiceAppConfig.layers[layerKey].visible
            })
          }
        })
        const sortedSublayers = sublayers.sort((a, b) => b.id - a.id)
        const mapImageLayer = new MapImageLayer({
          id: dynamicMapServiceKey,
          url: mapServiceAppConfig.url,
          sublayers: sortedSublayers
        })
        resolve(mapImageLayer)
      },
      reason => {
        reject(reason)
      }
    )
  })
}

export const createMapImageLayers = (MapImageLayer: any, legendConfigs: any): Promise<any> => {
  return new Promise((resolve, reject) => {
    let mapImageLayerPromises: Promise<any>[] = []

    Object.keys(mapConfig.dynamicMapServices)
      .filter((key: any) => mapConfig.dynamicMapServices[key].includeInMap)
      .forEach((dynamicMapServiceKey: any) => {
        mapImageLayerPromises.push(createMapImageLayer(MapImageLayer, dynamicMapServiceKey, legendConfigs))
      })

    Promise.all(mapImageLayerPromises).then(
      (mapImageLayers: any) => {
        resolve(mapImageLayers)
      },
      (reasons: any) => {
        reject(reasons)
      }
    )
  })
}

export const getLayerConfig = (mapServiceId: string, layerName: string): any => {
  const filteredLayerConfigKeys = Object.keys(mapConfig.dynamicMapServices[mapServiceId].layers).filter(
    (layerConfigKey: any) => {
      return mapConfig.dynamicMapServices[mapServiceId].layers[layerConfigKey].name === layerName
    }
  )
  if (filteredLayerConfigKeys.length) {
    return mapConfig.dynamicMapServices[mapServiceId].layers[filteredLayerConfigKeys[0]]
  } else {
    throw new Error(`Unable to get layer config for map service: ${mapServiceId} and layerName ${layerName}`)
  }
}

export const getSimpleMarkerSymbol = (color: string) => {
  return {
    type: 'simple-marker',
    style: 'circle',
    size: '12px',
    color
  }
}

export const getSimpleLineSymbol = (color: any, width: number = 10, style: string = 'solid') => {
  return {
    type: 'simple-line',
    color: color,
    width: `${width}px`,
    style: style
  }
}

export const getSimpleFillSymbol = (
  fillColor: any,
  outlineColor: any,
  outlineWidth: number = 10,
  fillStyle: string = 'solid',
  outlineStyle: string = 'solid'
) => {
  return {
    type: 'simple-fill',
    color: fillColor,
    style: fillStyle,
    outline: getSimpleLineSymbol(outlineColor, outlineWidth, outlineStyle)
  }
}

export const createSimplePolygonRenderer = (
  fillColor: any,
  outlineColor: any,
  outlineWidth?: number,
  fillStyle?: string,
  outlineStyle?: string
) => {
  return {
    type: 'simple',
    symbol: getSimpleFillSymbol(fillColor, outlineColor, outlineWidth, fillStyle, outlineStyle)
  }
}

export const queryFeatures = (
  url: string,
  geometry: any,
  where: string,
  outFields: string[] = ['objectid'],
  returnGeometry: boolean = true
) => {
  return new Promise((resolve, reject) => {
    window.require(['esri/tasks/QueryTask', 'esri/tasks/support/Query'], (QueryTask: any, Query: any) => {
      const queryTask = new QueryTask({
        url: url
      })
      const query = new Query()
      query.returnGeometry = returnGeometry
      query.outFields = outFields
      query.geometry = geometry
      query.where = where

      queryTask.execute(query).then(
        (results: any) => {
          resolve(results.features)
        },
        (reason: any) => {
          reject(reason)
        }
      )
    })
  })
}

export const mapQueryResultsToLevyRates = (results: any) => {
  let levyRates: any = {
    nvArea: -1,
    scatteredTreesCount: -1,
    gsmArea: -1,
    ggfArea: -1,
    mflArea: -1,
    sbbArea: -1,
    srfArea: -1
  }

  results.forEach((result: any) => {
    if (result.attributes.CODE === 'NV') {
      levyRates.nvArea = result.attributes.UNIT_RATE
    }
    if (result.attributes.CODE === 'ST') {
      levyRates.scatteredTreesCount = result.attributes.UNIT_RATE
    }
    if (result.attributes.CODE === 'GSM') {
      levyRates.gsmArea = result.attributes.UNIT_RATE
    }
    if (result.attributes.CODE === 'GGF') {
      levyRates.ggfArea = result.attributes.UNIT_RATE
    }
    if (result.attributes.CODE === 'MFL') {
      levyRates.mflArea = result.attributes.UNIT_RATE
    }
    if (result.attributes.CODE === 'SBB') {
      levyRates.sbbArea = result.attributes.UNIT_RATE
    }
    if (result.attributes.CODE === 'SRF') {
      levyRates.srfArea = result.attributes.UNIT_RATE
    }
  })

  if (Object.keys(levyRates).filter(k => levyRates[k] === -1).length > 0) {
    throw new Error('Error setting MSA Levy Rates')
  }

  return levyRates
}

const mapGpResultsToLevyLiabilityResults = (results: any) => {
  return {
    totalArea: results.results[0].value,
    levyLand: results.results[1].value,
    unpaidLevyLand: results.results[2].value,
    conservationAreas: results.results[3].value.map((x: string) => {
      const parts = x.split(':')
      return [parts[0], parts[1], parseFloat(parts[2])]
    }),
    ggfArea: results.results[4].value,
    ggfAmount: results.results[5].value,
    sbbArea: results.results[6].value,
    sbbAmount: results.results[7].value,
    gsmArea: results.results[8].value,
    gsmAmount: results.results[9].value,
    nvArea: results.results[10].value,
    nvAmount: results.results[11].value,
    srfArea: results.results[12].value,
    srfAmount: results.results[13].value,
    mflArea: results.results[14].value,
    mflAmount: results.results[15].value,
    scatteredTreesCount: results.results[16].value,
    scatteredTreesAmount: results.results[17].value,
    intPrecincts: results.results[18].value,
    intCrownLand: results.results[19].value,
    intNativeVegRegulationsArea: results.results[20].value,
    intSalvageAndTranslocationArea: results.results[21].value,
    intNonUnpaid: results.results[22].value
  }
}

export const filterZMCoordinates = (esriJson: any) => {
  if (esriJson.features && esriJson.features.length > 0) {
    esriJson.features = esriJson.features.map((k: any) => {
      if (k.geometry) {
        delete k.geometry.z
        delete k.geometry.m

        delete k.geometry.hasZ
        delete k.geometry.hasM

        delete k.geometry.zmin
        delete k.geometry.zmax
        delete k.geometry.mmin
        delete k.geometry.mmax
      }
      return k
    })
  }
  return esriJson
}

export const calculateLevyLiabilities = (url: string, parcelSpi?: string, esriJson?: string) => {
  return new Promise((resolve, reject) => {
    window.require(['esri/tasks/Geoprocessor'], (Geoprocessor: any) => {
      const geoprocessor = new Geoprocessor({
        url: url
      })

      let executeOptions

      if (!!parcelSpi) {
        executeOptions = {
          parcelId: parcelSpi
        }
      } else if (!!esriJson) {
        executeOptions = {
          esriJson: esriJson
        }
      } else {
        throw new Error('Calculate Levy Liabilities requires either Parcel SPI or ESRI JSON Geometry')
      }

      geoprocessor.execute(executeOptions).then(
        (results: any) => {
          resolve(mapGpResultsToLevyLiabilityResults(results))
        },
        (reason: any) => {
          reject(reason)
        }
      )
    })
  })
}

export const projectGeometry = (esriLibrary: any, geometry: any, spatialReference: any): Promise<any> => {
  const component = this
  let promise = new Promise((resolve, reject) => {
    try {
      if (esriLibrary.projection.isLoaded()) {
        resolve(esriLibrary.projection.project(geometry, spatialReference))
      } else {
        resolve(esriLibrary.projection.load().then(() => esriLibrary.projection.project(geometry, spatialReference)))
      }
    } catch (error: any) {
      reject(error)
    }
  })
  return promise
}
